import { takeEvery, put, call, getContext, all, select, take, spawn } from 'redux-saga/effects';

import { getLogger } from '../../logger/index';

import {
  OPEN_CHAT,
  OPEN_CONVERSATION,
  CHANGE_CHATTER_MESSAGE_STATUS,
  openConversationSuccess,
  openConversationFailure,
  changeChatterMessageStatusFailure,
  changeChatterMessageStatusSuccess,
  changeChatterMessageStatus,
  openConversation,
  OPEN_CONVERSATION_SUCCESS,
  closeChat,
  setCustomerAgent,
  closeCustomerChat,
} from './actions';
import { sendUsersList, SOCKET_CONNECTED, SOCKET_EVENT, INIT_PLUGIN_COMMS_CENTER } from '../communication/actions';
import communication from '../communication';
import selectors from './selectors';
import { findConversation } from './utils';
import customerSaga from './customerSaga';
import { logoutSuccess } from '../auth/actions';
import authSelectors from '../auth/selectors';
import { callForEmployee, callForCustomer } from '../auth/sagaUtils';

const logger = getLogger('chat');

function* fetchContactSaga() {
  try {
    const isEmployee = yield select(authSelectors.isEmployee);
    const userList = yield select(communication.contact.selectors.getList);
    const isListPending = yield select(communication.contact.selectors.isListLoading);

    if (isEmployee) {
      if (!isListPending && (!userList || userList.length === 0)) {
        logger.debug(
          'No local list of contacts, so fetch contacts',
          communication.contact.actionTypes.FETCH_LIST_COMPLETE
        );
        yield put(communication.contact.fetchList());
      } else if (userList && userList.length > 0) {
        logger.debug('Send users list', userList);
        yield put(sendUsersList(userList.map(user => user.id)));
      }
    }
  } catch (e) {
    logger.error(e);
  }
}

function* clearContactsSaga() {
  try {
    yield put(communication.contact.fetchListComplete({ count: 0, list: [] }));
  } catch (e) {
    logger.error(e);
  }
}

function* communicationFetchContactListCompleteSaga({ payload: { data } }) {
  try {
    logger.debug('Got fetch contacts list success');
    yield put(sendUsersList(data.list.map(user => user.id)));
  } catch (e) {
    logger.error(e);
  }
}

function* openConversationSaga({ payload: { username, isAnonymousUser, customerChatId, customerVideoCallId } }) {
  try {
    logger.debug('openConversationSaga', { username, isAnonymousUser, customerChatId, customerVideoCallId });
    if (isAnonymousUser) {
      logger.debug('Open conversation success for anonymous customer');
      const history = yield select(selectors.getMessages);
      yield put(openConversationSuccess(username, history, { isAnonymousUser, customerChatId, customerVideoCallId }));
      return;
    }
    const http = yield getContext('http');
    const requestUrl = `/api/chatterhistory.json?user=${username}`;
    const response = yield call(http.get, requestUrl);
    if (response.status < 200 || response.status > 299) {
      logger.error('Response with HTTP status', response.status, response);
      yield put(openConversationFailure(response.data));
    } else {
      logger.debug('Open conversation success', response.data.list);
      yield put(
        openConversationSuccess(username, response.data.list, { isAnonymousUser, customerChatId, customerVideoCallId })
      );
    }
  } catch (error) {
    logger.error(error);
    yield put(openConversationFailure(error));
  }
}

function* openConversationSuccessSaga({ payload: { username, history } }) {
  try {
    if (Array.isArray(history)) {
      yield all(
        history
          .filter(entry => entry.id && entry.status !== 'seen' && entry.creator === username)
          .map(entry => put(changeChatterMessageStatus(entry.id, 'seen')))
      );
    }
  } catch (e) {
    logger.error(e);
  }
}

function* changeChatterMessageStatusSaga({ payload: { messageId, newStatus: status } }) {
  try {
    const http = yield getContext('http');
    const requestUrl = `/api/chattermessages/${messageId}.json`;
    const response = yield call(http.put, requestUrl, { status });
    if (response.status < 200 || response.status > 299) {
      logger.error('Response with HTTP status', response.status, response);
      yield put(changeChatterMessageStatusFailure(response.data));
    } else {
      logger.debug('Open conversation success', response.data);
      yield put(changeChatterMessageStatusSuccess(response.data));
    }
  } catch (error) {
    logger.error(error);
    yield put(changeChatterMessageStatusFailure(error));
  }
}

function* openChatSaga({ payload: { username, isAnonymousUser, customerChatId, customerVideoCallId } }) {
  try {
    const activeConversations = yield select(selectors.getActiveConversations);
    const conversation = yield call(findConversation, activeConversations, username);
    if (!conversation || conversation.silent) {
      yield put(openConversation(username, isAnonymousUser, customerChatId, customerVideoCallId));
    } else {
      yield all(
        conversation.messages
          .filter(entry => entry.id && entry.status !== 'seen' && entry.creator === username)
          .map(entry => put(changeChatterMessageStatus(entry.id, 'seen')))
      );
    }
  } catch (e) {
    logger.error(e);
  }
}

function* customerChatEndedSaga() {
  try {
    logger.debug('Close customer chat after receiving chat_ended socket event');
    yield put(closeChat());
    yield put(setCustomerAgent(null));
  } catch (e) {
    logger.error(e);
  }
}

function* employeeChatEndedSaga({ to }) {
  try {
    logger.debug('Close the chat with customer by employee');
    yield put(closeCustomerChat(to));
  } catch (e) {
    logger.error(e);
  }
}

function* watchChatActions() {
  try {
    logger.debug('Watching chat actions', communication);
    yield takeEvery(SOCKET_CONNECTED, fetchContactSaga);
    yield takeEvery(logoutSuccess, clearContactsSaga);
    yield takeEvery(communication.contact.actionTypes.FETCH_LIST_COMPLETE, communicationFetchContactListCompleteSaga);
    yield takeEvery(OPEN_CHAT, openChatSaga);
    yield takeEvery(OPEN_CONVERSATION, openConversationSaga);
    yield takeEvery(OPEN_CONVERSATION_SUCCESS, openConversationSuccessSaga);
    yield takeEvery(CHANGE_CHATTER_MESSAGE_STATUS, changeChatterMessageStatusSaga);

    // for customer
    yield takeEvery(
      action => action.type === 'SOCKET_EVENT' && action.payload.eventName === 'chat_ended',
      function*(action) {
        yield all([
          callForCustomer(customerChatEndedSaga, action.payload.args),
          callForEmployee(employeeChatEndedSaga, action.payload.args),
        ]);
      }
    );
  } catch (e) {
    logger.error(e);
  }
}

function* watchPluginInit() {
  const { payload: plugin } = yield take(INIT_PLUGIN_COMMS_CENTER);
  logger.debug('Plugin "' + plugin.name + '" is initializing');
  yield spawn(customerSaga);
  logger.debug('Plugin "' + plugin.name + '" is initialized');
}

export default [watchChatActions, watchPluginInit];
